import api from '../../lib/api';
import type {
  CreateDecisionPayload,
  CreateReviewPayload,
  ManagementReviewDecision,
  ManagementReviewDetail,
  ManagementReviewParticipant,
  MgmtReviewInputs,
  PaginatedManagementReviews,
} from './types';

export interface ListReviewsParams {
  page?: number;
  pageSize?: number;
  status?: string;
  search?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

export const fetchInputsPreview = async (
  from: string,
  to: string,
): Promise<MgmtReviewInputs> => {
  const { data } = await api.get<{ data: MgmtReviewInputs }>('management-reviews/inputs-preview', {
    params: { from, to },
  });
  return data.data;
};

export const fetchManagementReviews = async (
  params: ListReviewsParams = {},
): Promise<PaginatedManagementReviews> => {
  const { data } = await api.get<PaginatedManagementReviews>('management-reviews', { params });
  return data;
};

export const fetchManagementReview = async (id: string): Promise<ManagementReviewDetail> => {
  const { data } = await api.get<ManagementReviewDetail>(`management-reviews/${id}`);
  return data;
};

export const createManagementReview = async (payload: CreateReviewPayload) => {
  const { data } = await api.post('management-reviews', payload);
  return data;
};

export const updateManagementReview = async (
  id: string,
  payload: Partial<CreateReviewPayload> & { status?: string; conclusions?: string | null },
): Promise<Pick<ManagementReviewDetail, 'conclusions' | 'status'>> => {
  const { data } = await api.put<Pick<ManagementReviewDetail, 'conclusions' | 'status'>>(
    `management-reviews/${id}`,
    payload,
  );
  return data;
};

export const addParticipant = async (
  reviewId: string,
  userId: number,
  attended = true,
): Promise<ManagementReviewParticipant> => {
  const { data } = await api.post<ManagementReviewParticipant>(
    `management-reviews/${reviewId}/participants`,
    { userId, attended },
  );
  return data;
};

export const removeParticipant = async (reviewId: string, participantId: string) => {
  await api.delete(`management-reviews/${reviewId}/participants/${participantId}`);
};

export const createDecision = async (
  reviewId: string,
  payload: CreateDecisionPayload,
): Promise<ManagementReviewDecision> => {
  const { data } = await api.post<ManagementReviewDecision>(
    `management-reviews/${reviewId}/decisions`,
    payload,
  );
  return data;
};

export const updateDecision = async (
  decisionId: string,
  payload: Partial<CreateDecisionPayload> & { status?: string },
): Promise<ManagementReviewDecision> => {
  const { data } = await api.patch<ManagementReviewDecision>(
    `management-reviews/decisions/${decisionId}`,
    payload,
  );
  return data;
};

export const decisionToObjective = async (
  decisionId: string,
): Promise<{ objectiveId: string; decision?: ManagementReviewDecision }> => {
  const { data } = await api.post<{ objectiveId: string; decision?: ManagementReviewDecision }>(
    `management-reviews/decisions/${decisionId}/to-objective`,
  );
  return data;
};

export const decisionToNc = async (
  decisionId: string,
): Promise<{ qmsRecordId: string; decision?: ManagementReviewDecision }> => {
  const { data } = await api.post<{ qmsRecordId: string; decision?: ManagementReviewDecision }>(
    `management-reviews/decisions/${decisionId}/to-nc`,
  );
  return data;
};
